/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useIntl, FormattedNumber} from 'react-intl'

// Chakra Components
import {
    Box,
    Stack,
    Grid,
    GridItem,
    Container,
    useDisclosure,
    Spinner,
    Skeleton
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import CartCta from './partials/cart-cta'
import CartTitle from './partials/cart-title'
import ConfirmationModal from '@salesforce/retail-react-app/app/components/confirmation-modal'
import EmptyCart from './partials/empty-cart'
import OrderSummary from '../../components/order-summary'
import StickyCheckoutBar from './partials/sticky-checkout-bar'

import ProductViewModal from '../../components/product-view-modal'
import ApproachingDiscounts from '../../components/approaching-discounts'
import BundleProductViewModal from '@salesforce/retail-react-app/app/components/product-view-modal/bundle'
import NoReturnNotification from './partials/cart-no-return-notification'
import LoyaltyCoupon from '../../components/loyalty-coupon'
import ProductItemList from '../../components/product-item-list'
import {useCartCMSContent} from './partials/use-cart-cms-content'
import {useCartVickyInjection} from './partials/use-cart-vicky-injection'
import {
    renderCarouselItems,
    renderLayoutsSwitch
} from '../../components/optimizely/utils/contentRenderer'

// Hooks
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import {useRemoveGiftCards} from '../../hooks/use-remove-gift-cards'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useOptimizelyPage} from '../../hooks/use-optimizely-page'
import useHideLoyaltyUIForInternational from '@app/hooks/use-hide-loyalty-ui-for-international'
import Seo from '../../components/seo'

// Constants
import {API_ERROR_MESSAGE, TOAST_MESSAGE_REMOVED_ITEM_FROM_CART} from '../../constants'

import {REMOVE_CART_ITEM_CONFIRMATION_DIALOG_CONFIG} from '@salesforce/retail-react-app/app/pages/cart/partials/cart-secondary-button-group'

// Utilities
import debounce from 'lodash/debounce'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {
    useShopperBasketsMutation,
    useShippingMethodsForShipment
} from '@salesforce/commerce-sdk-react'
import {useWishList} from '@salesforce/retail-react-app/app/hooks/use-wish-list'
import {getUpdateBundleChildArray} from '@salesforce/retail-react-app/app/utils/product-utils'
import {useLocation} from 'react-router-dom'
import {sendItemCartEvent, sendViewCartEvent} from '../../utils/gtm-utils'
import {sendBrazeCartEvent} from '../../utils/braze-utils'
import {getBasketItemInventoryFieldsForLocale} from '../../../../utils/intl-inventory'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getIsFixedPriceModel} from '../../esw/esw-helpers'
import useIsInternational from '../../hooks/use-is-international'

const DEBOUNCE_WAIT = 750
const Cart = () => {
    const {pathname} = useLocation()
    const {app: appConfig} = getConfig()
    const {data: basket, refetch, isLoading, isFetching} = useCurrentBasket()
    const {locale: currentLocale} = useMultiSite()
    const basketItemInventoryFields = getBasketItemInventoryFieldsForLocale(currentLocale)
    const {data: optimizelyData, isLoading: isOptimizelyLoading} = useOptimizelyPage('cart')

    // Inject VickyAI data into CMS content
    const processedOptimizelyData = useCartVickyInjection(optimizelyData)

    const {banners, carousels} = useCartCMSContent(processedOptimizelyData)

    const {data: customer} = useCurrentCustomer()
    const shouldHideLoyaltyUI = useHideLoyaltyUIForInternational()
    const removeGiftCards = useRemoveGiftCards(basket)
    const [isApplied, setApplied] = useState(false)
    const [isFixedPriceModel, setIsFixedPriceModel] = useState(false)
    const isInternational = useIsInternational()
    useEffect(() => {
        setIsFixedPriceModel(getIsFixedPriceModel())
    }, [])

    useEffect(() => {
        // Refetch basket to get the c_sflmData
        if (!basket?.c_sflmData) {
            refetch()
        }

        removeGiftCards()
    }, [basket])

    /***************** Product Bundles ************************/
    const bundleChildVariantIds = []
    basket?.productItems?.forEach((productItem) => {
        productItem?.bundledProductItems?.forEach((childProduct) => {
            bundleChildVariantIds.push(childProduct.productId)
        })
    })

    /*****************Basket Mutation************************/
    const updateItemInBasketMutation = useShopperBasketsMutation('updateItemInBasket')
    const updateItemsInBasketMutation = useShopperBasketsMutation('updateItemsInBasket')
    const removeItemFromBasketMutation = useShopperBasketsMutation('removeItemFromBasket')
    const updateShippingMethodForShipmentsMutation = useShopperBasketsMutation(
        'updateShippingMethodForShipment'
    )
    const applyLoyaltyCodeMutation = useShopperBasketsMutation('addCouponToBasket')
    const removeLoyaltyCodeMutation = useShopperBasketsMutation('removeCouponFromBasket')
    /*****************Basket Mutation************************/

    const [selectedItem, setSelectedItem] = useState(undefined)
    const [localQuantity, setLocalQuantity] = useState({})
    const [isCartItemLoading, setCartItemLoading] = useState(false)
    const [intialApproachingDiscounts, setIntialApproachingDiscounts] = useState({
        shipments: basket?.shipments || [],
        isApplied: false
    })

    const {isOpen, onOpen, onClose} = useDisclosure()
    const {formatMessage} = useIntl()
    const toast = useToast()
    const modalProps = useDisclosure()

    const cartPageTitle = formatMessage({
        id: 'cart.page.title',
        defaultMessage: 'My Shopping Bag | Garage Clothing UK'
    })
    const cartPageDescription = formatMessage({
        id: 'cart.page.description',
        defaultMessage: 'My Shopping Bag | Garage Clothing UK'
    })

    const [hasFinalSaleInCart, setHasFinalSaleInCart] = useState(false)

    const isShippingDiscountApplied =
        !isLoading &&
        basket?.shippingItems?.some((item) => {
            const freeAdjustments = item.priceAdjustments?.filter(
                (adjustment) => adjustment.appliedDiscount?.type === 'free'
            )
            return freeAdjustments && freeAdjustments.length > 0
        })

    const hasApproachingDiscountData = useMemo(
        () =>
            basket?.shipments?.some((shipment) =>
                shipment?.shippingMethod?.shippingPromotions?.some(
                    (promotion) => promotion?.c_approachingDiscounts?.qualifiesForFreeShipping
                )
            ),
        [basket?.shipments]
    )

    useEffect(() => {
        setIntialApproachingDiscounts((current) => ({
            shipments:
                hasApproachingDiscountData || (!isFetching && !current.shipments?.length)
                    ? basket?.shipments || []
                    : current.shipments,
            isApplied:
                typeof isShippingDiscountApplied === 'boolean' &&
                (isShippingDiscountApplied || !isFetching)
                    ? isShippingDiscountApplied
                    : current.isApplied
        }))
    }, [
        basket?.shipments,
        hasApproachingDiscountData,
        isFetching,
        isShippingDiscountApplied
    ])

    /******************* Shipping Methods for basket shipment *******************/
    // Fetch when the basket has no shipping method, or when the assigned method
    // hasn't been validated yet for this basket - not on every render/mount - so we
    // can detect and correct a stale/no-longer-applicable shipping method (e.g. a
    // non-domestic default assigned before the session's currency/locale had
    // settled) without re-checking a method we've already confirmed is fine.
    const validatedShippingMethodRef = useRef(null)
    const currentShippingMethodId = basket?.shipments?.[0]?.shippingMethod?.id
    const shippingMethodValidationKey = basket?.basketId
        ? `${basket.basketId}:${currentShippingMethodId || ''}`
        : null

    useShippingMethodsForShipment(
        {
            parameters: {
                basketId: basket?.basketId,
                shipmentId: 'me'
            }
        },
        {
            enabled:
                !!basket?.basketId &&
                basket.shipments.length > 0 &&
                validatedShippingMethodRef.current !== shippingMethodValidationKey,
            onSuccess: (data) => {
                const isCurrentMethodApplicable = data?.applicableShippingMethods?.some(
                    (method) => method.id === currentShippingMethodId
                )

                validatedShippingMethodRef.current = shippingMethodValidationKey

                if (!isCurrentMethodApplicable && data?.defaultShippingMethodId) {
                    updateShippingMethodForShipmentsMutation.mutate({
                        parameters: {
                            basketId: basket?.basketId,
                            shipmentId: 'me'
                        },
                        body: {
                            id: data.defaultShippingMethodId
                        }
                    })
                }
            }
        }
    )

    /************************* Error handling ***********************/
    const showError = () => {
        toast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    }
    /************************* Error handling ***********************/

    /**************** Wishlist ****************/
    const {data: wishlist, isLoading: isWishListLoading} = useWishList()
    /**************** Wishlist ****************/

    /***************************** Update Cart **************************/
    const handleUpdateCart = async (variant, quantity) => {
        // close the modal before handle the change
        onClose()
        // using try-catch is better than using onError callback since we have many mutation calls logic here
        try {
            setCartItemLoading(true)
            const productIds = basket.productItems.map(({productId}) => productId)

            // The user is selecting different variant, and it has not existed in basket
            if (selectedItem.id !== variant.productId && !productIds.includes(variant.productId)) {
                const item = {
                    productId: variant.productId,
                    ...basketItemInventoryFields,
                    quantity,
                    price: variant.price
                }
                return await updateItemInBasketMutation.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        itemId: selectedItem.itemId
                    },
                    body: item
                })
            }

            // The user is selecting different variant, and it has existed in basket
            // remove this item in the basket, change the quantity for the new selected variant in the basket
            if (selectedItem.id !== variant.productId && productIds.includes(variant.productId)) {
                await removeItemFromBasketMutation.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        itemId: selectedItem.itemId
                    }
                })
                const basketItem = basket.productItems.find(
                    ({productId}) => productId === variant.productId
                )
                const newQuantity = quantity + basketItem.quantity
                return await changeItemQuantity(newQuantity, basketItem)
            }

            // the user only changes quantity of the same variant
            if (selectedItem.quantity !== quantity) {
                return await changeItemQuantity(quantity, selectedItem)
            }
        } catch {
            showError()
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    const handleUpdateBundle = async (bundle, bundleQuantity, childProducts) => {
        // close the modal before handle the change
        onClose()

        try {
            setCartItemLoading(true)
            const itemsToBeUpdated = getUpdateBundleChildArray(bundle, childProducts)

            // We only update the parent bundle when the quantity changes
            // Since top level bundles don't have variants
            if (bundle.quantity !== bundleQuantity) {
                itemsToBeUpdated.unshift({
                    itemId: bundle.itemId,
                    productId: bundle.productId,
                    ...basketItemInventoryFields,
                    quantity: bundleQuantity
                })
            }

            const itemsToBeUpdatedWithInventory = itemsToBeUpdated.map((item) => ({
                ...item,
                ...basketItemInventoryFields
            }))

            if (itemsToBeUpdatedWithInventory.length) {
                await updateItemsInBasketMutation.mutateAsync({
                    method: 'PATCH',
                    parameters: {
                        basketId: basket.basketId
                    },
                    body: itemsToBeUpdatedWithInventory
                })
            }
        } catch {
            showError()
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    /***************************** Update Cart **************************/

    /***************************** Update quantity **************************/
    const changeItemQuantity = debounce(async (quantity, product) => {
        // This local state allows the dropdown to show the desired quantity
        // while the API call to update it is happening.
        const previousQuantity = localQuantity[product.itemId]
        setLocalQuantity({...localQuantity, [product.itemId]: quantity})
        setCartItemLoading(true)
        setSelectedItem(product)

        await updateItemInBasketMutation.mutateAsync(
            {
                parameters: {basketId: basket?.basketId, itemId: product.itemId},
                body: {
                    productId: product.id,
                    ...basketItemInventoryFields,
                    quantity: parseInt(quantity)
                }
            },
            {
                onSettled: () => {
                    // reset the state
                    setCartItemLoading(false)
                    setSelectedItem(undefined)
                },
                onSuccess: () => {
                    setLocalQuantity({...localQuantity, [product.itemId]: undefined})
                },
                onError: () => {
                    // reset the quantity to the previous value
                    setLocalQuantity({...localQuantity, [product.itemId]: previousQuantity})
                    showError()
                }
            }
        )
    }, DEBOUNCE_WAIT)

    /***************************** Update quantity **************************/

    /***************************** Remove Item from basket **************************/
    const handleRemoveItem = async (product) => {
        setSelectedItem(product)
        setCartItemLoading(true)
        await removeItemFromBasketMutation.mutateAsync(
            {
                parameters: {basketId: basket.basketId, itemId: product.itemId}
            },
            {
                onSettled: () => {
                    // reset the state
                    setCartItemLoading(false)
                    setSelectedItem(undefined)
                },
                onSuccess: () => {
                    toast({
                        title: formatMessage(TOAST_MESSAGE_REMOVED_ITEM_FROM_CART, {quantity: 1}),
                        status: 'success'
                    })
                    // GTM Modification
                    sendItemCartEvent(product, true)

                    // Braze Modification - Send Braze Cart Event
                    sendBrazeCartEvent(basket, product, 'remove')
                },
                onError: () => {
                    showError()
                }
            }
        )
    }

    const [savings, setSavings] = useState(0)
    const [isLoyaltyLoading, setIsLoyaltyLoading] = useState(false)
    const [initialSflmData, setIntialSflmData] = useState(null)

    // Derive sflmData directly from basket to avoid extra state + effect
    const sflmData = basket?.c_sflmData || null

    useEffect(() => {
        if (sflmData) {
            setIntialSflmData(sflmData)
        }
    }, [sflmData])
    const loyaltyDisplayData = sflmData || initialSflmData

    const loyaltyCouponItem = useMemo(
        () =>
            basket?.couponItems?.find(
                (item) =>
                    item?.couponItemId === basket?.c_sflmLoyaltyCouponID ||
                    (item?.statusCode === 'applied' &&
                        item?.code === loyaltyDisplayData?.couponCode)
            ),
        [basket?.couponItems, basket?.c_sflmLoyaltyCouponID, loyaltyDisplayData?.couponCode]
    )

    const isLoyaltyCouponApplied = useMemo(() => {
        const hasLoyaltyCouponId = Boolean(basket?.c_sflmLoyaltyCouponID)
        const hasLoyaltyPayment = basket?.paymentInstruments?.some(
            (pi) => pi.paymentMethodId === 'LOYALTY_POINTS'
        )

        return hasLoyaltyCouponId || hasLoyaltyPayment || Boolean(loyaltyCouponItem)
    }, [
        basket?.c_sflmLoyaltyCouponID,
        basket?.paymentInstruments,
        loyaltyCouponItem
    ])

    const loyaltyCouponItemId = loyaltyCouponItem?.couponItemId || basket?.c_sflmLoyaltyCouponID

    // Sync isApplied from persisted basket fields after the basket refetch settles.
    useEffect(() => {
        if (basket?.c_sflmData) {
            setApplied(isLoyaltyCouponApplied)
        }
    }, [basket?.c_sflmData, isLoyaltyCouponApplied])

    // Derive eligibility during render instead of in useEffect (prevents CLS)
    const isEligibleForLoyalty = useMemo(() => {
        if (!loyaltyDisplayData) return false
        return (
            (customer.isRegistered &&
                basket?.productSubTotal > loyaltyDisplayData?.cashDiscountAmount &&
                loyaltyDisplayData?.nonQualifyingPointsBalance >= 150) ||
            isApplied ||
            false
        )
    }, [loyaltyDisplayData, customer.isRegistered, basket?.productSubTotal, isApplied])

    const shouldShowLoyaltyCoupon = !shouldHideLoyaltyUI && !!loyaltyDisplayData && (isEligibleForLoyalty || isLoyaltyLoading || isApplied)
    const seo = <Seo title={cartPageTitle} description={cartPageDescription} noIndex noFollow />

    // GTM Modification
    useEffect(() => {
        if (basket?.productItems) {
            sendViewCartEvent(basket)
        }
    }, [])

    const reedemPoints = async () => {
        if (!sflmData?.couponCode) {
            return
        }

        try {
            setIsLoyaltyLoading(true)
            await applyLoyaltyCodeMutation.mutateAsync(
                {
                    parameters: {basketId: basket?.basketId},
                    body: {
                        code: sflmData?.couponCode
                    }
                },
                {
                    onSuccess: () => {
                        setIsLoyaltyLoading(false)
                        setApplied(true)
                    }
                }
            )
        } catch (e) {
            setIsLoyaltyLoading(false)
            setApplied(false)
            console.error('[reedemPoints] Error when trying to redeem loyalty points', e)
        }
    }

    const removePoints = () => {
        if (!loyaltyCouponItemId) {
            return
        }

        setIsLoyaltyLoading(true)

        removeLoyaltyCodeMutation.mutate(
            {
                parameters: {
                    basketId: basket?.basketId,
                    couponItemId: loyaltyCouponItemId
                }
            },
            {
                onSettled: () => {
                    setIsLoyaltyLoading(false)
                    setApplied(false)
                }
            }
        )
    }

    /********* Rendering  UI **********/
    // Only show empty cart once loading has finished and we know the basket is truly empty.
    // During fresh loads, isLoading starts false briefly before the query fires,
    // so we also require basket to have been fetched (basket !== undefined).
    if (!isLoading && (!basket || !basket.productItems?.length)) {
        return <EmptyCart />
    }

    return (
        <Box flex="1" data-testid="sf-cart-container">
            {seo}
            {/* Sticky Checkout Bar */}
            <StickyCheckoutBar basket={basket} />

            <Container
                maxWidth="container.xl"
                px={{base: 4, lg: 6}}
                paddingTop={{base: 8, lg: 8}}
                paddingBottom={{base: 8, lg: 14}}
            >
                <Stack spacing={24}>
                    <Stack spacing={8}>
                        {/* Category Banners - Top Area
                            Always reserve minHeight to prevent CLS when Optimizely
                            banner arrives after the VickyAI injection pipeline */}
                        <Box
                            minHeight={{base: '100px', lg: '100px'}}
                            mb={banners.length > 0 ? 8 : 0}
                        >
                            {isOptimizelyLoading ? (
                                <Skeleton height={{base: '100px', lg: '100px'}} borderRadius="md" />
                            ) : (
                                banners.map((banner, index) => (
                                    <React.Fragment key={`cart-banner-${index}`}>
                                        {renderLayoutsSwitch(banner)}
                                    </React.Fragment>
                                ))
                            )}
                        </Box>

                        <Grid
                            templateColumns={{base: '1fr', lg: '66% 1fr'}}
                            gap={{base: 10, xl: 20}}
                            width={'full'}
                        >
                            <GridItem maxW={'full'} minWidth={0}>
                                <CartTitle />
                                {!isFixedPriceModel && (
                                    <Box minHeight={{base: '60px', lg: '70px'}}>
                                        <ApproachingDiscounts
                                            currency={basket?.currency}
                                            shipments={intialApproachingDiscounts.shipments}
                                            isApplied={intialApproachingDiscounts.isApplied}
                                        />
                                    </Box>
                                )}
                                <Stack spacing={{base: '10px', lg: '14px'}} mb="7">
                                    <ProductItemList
                                        isOpen={isOpen}
                                        onOpen={onOpen}
                                        onClose={onClose}
                                        setHasFinalSaleInCart={setHasFinalSaleInCart}
                                    />
                                </Stack>
                                {hasFinalSaleInCart && <NoReturnNotification />}
                                <Box>
                                    {isOpen &&
                                        selectedItem &&
                                        !selectedItem.bundledProductItems && (
                                            <ProductViewModal
                                                isOpen={isOpen}
                                                onOpen={onOpen}
                                                onClose={onClose}
                                                product={selectedItem}
                                                updateCart={(variant, quantity) =>
                                                    handleUpdateCart(variant, quantity)
                                                }
                                            />
                                        )}
                                    {isOpen && selectedItem?.bundledProductItems && (
                                        <BundleProductViewModal
                                            isOpen={isOpen}
                                            onOpen={onOpen}
                                            onClose={onClose}
                                            product={selectedItem}
                                            updateCart={(product, quantity, childProducts) =>
                                                handleUpdateBundle(product, quantity, childProducts)
                                            }
                                        />
                                    )}
                                </Box>
                            </GridItem>
                            <GridItem maxW={'full'}>
                                <Stack spacing={10}>
                                    {customer.isRegistered && !isFixedPriceModel && (
                                        <Box
                                            minHeight={
                                                shouldShowLoyaltyCoupon || !sflmData
                                                    ? '80px'
                                                    : undefined
                                            }
                                        >
                                            {shouldShowLoyaltyCoupon && (
                                                <LoyaltyCoupon
                                                    isPointsApplied={isApplied}
                                                    availablePoints={
                                                        loyaltyDisplayData?.nonQualifyingPointsBalance
                                                    }
                                                    numberOfRedeemedPoints={
                                                        loyaltyDisplayData?.nonQualifyingPointsToDebit
                                                    }
                                                    discount={
                                                        basket?.currency ? (
                                                            <FormattedNumber
                                                                value={
                                                                    loyaltyDisplayData?.cashDiscountAmount
                                                                }
                                                                style="currency"
                                                                currency={basket.currency}
                                                            />
                                                        ) : (
                                                            '—'
                                                        )
                                                    }
                                                    reedemPoints={reedemPoints}
                                                    removePoints={removePoints}
                                                />
                                            )}
                                        </Box>
                                    )}
                                    {   /** for international, we hide many components on the cart */
                                        basket ? (
                                        <OrderSummary
                                            isInternational={isInternational}
                                            showPromoCodeForm={!isInternational}
                                            showPostalCodeForm={!isInternational}
                                            isEstimate={!isInternational}
                                            basket={basket}
                                            savings={savings}
                                            page={'cart'}
                                        />
                                    ) : (
                                        <Skeleton height="300px" borderRadius="md" />
                                    )}
                                    <Box>
                                        <CartCta />
                                    </Box>
                                </Stack>
                            </GridItem>
                        </Grid>

                        {/* VickyAI Carousels - Bottom Area */}
                        {appConfig?.VICKY_ENABLED === 'true' && (
                            <Box
                                minHeight={
                                    isOptimizelyLoading || carousels.length > 0
                                        ? '350px'
                                        : undefined
                                }
                                sx={{
                                    contentVisibility: 'auto',
                                    containIntrinsicSize: '0 350px'
                                }}
                            >
                                {isOptimizelyLoading && (
                                    <Box>
                                        <Skeleton height="30px" width="300px" mb={4} />
                                        <Box display="flex" gap={4} overflow="hidden" width="100%">
                                            {[0, 1, 2, 3, 4].map((key) => (
                                                <Box
                                                    key={key}
                                                    width="calc(20% - 16px)"
                                                    flexShrink={0}
                                                >
                                                    <Stack spacing={4}>
                                                        <Skeleton height="250px" />
                                                        <Skeleton height="20px" width="80%" />
                                                        <Skeleton height="20px" width="60%" />
                                                    </Stack>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                                {!isOptimizelyLoading && carousels.length > 0 && (
                                    <Stack spacing={8}>
                                        {carousels.map((carousel, index) => (
                                            <Box key={`cart-carousel-${index}`}>
                                                {renderCarouselItems(
                                                    carousel,
                                                    `cart-carousel-${index}`
                                                )}
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                        )}
                    </Stack>
                </Stack>
            </Container>
            <ConfirmationModal
                {...REMOVE_CART_ITEM_CONFIRMATION_DIALOG_CONFIG}
                onPrimaryAction={() => {
                    handleRemoveItem(selectedItem)
                }}
                onAlternateAction={() => {}}
                {...modalProps}
            />
        </Box>
    )
}

Cart.getTemplateName = () => 'cart'

export default Cart
